import { supabase, retryOperation } from '../lib/supabase';

interface FrostDates {
  lastSpring: string | null;
  firstFall: string | null;
}

interface GeoLocation {
  lat: number;
  lon: number;
}

interface Station {
  id: string;
  name: string;
  distance: number;
}

interface FrostProbability {
  prob_10: string;
  prob_50: string;
  prob_90: string;
}

function formatFrostDate(dayOfYear: string): string {
  // Convert day of year (e.g., "1010") to a proper ISO date
  // First, ensure we have a 4-digit format (MMDD)
  const paddedDay = dayOfYear.padStart(4, '0');
  
  // Extract month and day
  const month = parseInt(paddedDay.substring(0, 2));
  const day = parseInt(paddedDay.substring(2, 4));

  // Validate month and day
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    throw new Error(`Invalid date format: ${dayOfYear}`);
  }

  // Create a date string in ISO format for the current year
  const currentYear = new Date().getFullYear();
  return `${currentYear}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
}

async function getGeoLocation(zipCode: string): Promise<GeoLocation> {
  const apiKey = import.meta.env.VITE_OPENWEATHER_API_KEY;
  if (!apiKey) {
    throw new Error('OpenWeather API key is not configured');
  }

  const response = await fetch(
    `https://api.openweathermap.org/geo/1.0/zip?zip=${zipCode},US&appid=${apiKey}`
  );

  if (!response.ok) {
    throw new Error(`Failed to get location: ${response.statusText}`);
  }

  const data = await response.json();
  return {
    lat: data.lat,
    lon: data.lon
  };
}

async function getNearestStation(lat: number, lon: number): Promise<string> {
  const response = await fetch(
    `https://api.farmsense.net/v1/frostdates/stations/?lat=${lat}&lon=${lon}`
  );

  if (!response.ok) {
    throw new Error(`Failed to get stations: ${response.statusText}`);
  }

  const stations: Station[] = await response.json();
  if (!stations.length) {
    throw new Error('No weather stations found nearby');
  }

  // Get the closest station
  return stations.reduce((nearest, current) => 
    current.distance < nearest.distance ? current : nearest
  ).id;
}

async function getFrostProbabilities(stationId: string, season: 1 | 2): Promise<FrostProbability> {
  const response = await fetch(
    `https://api.farmsense.net/v1/frostdates/probabilities/?station=${stationId}&season=${season}`
  );

  if (!response.ok) {
    throw new Error(`Failed to get frost probabilities: ${response.statusText}`);
  }

  const data = await response.json();
  
  // Handle array response
  if (Array.isArray(data)) {
    if (data.length === 0) {
      throw new Error('No frost probability data available');
    }
    return data[0];
  }

  return data;
}

export async function getFrostDates(zipCode: string): Promise<FrostDates | null> {
  try {
    // First try to get from our cache
    const { data: cached, error: cacheError } = await retryOperation(() =>
      supabase
        .from('frost_dates_cache')
        .select('last_spring, first_fall')
        .eq('zip_code', zipCode)
        .maybeSingle()
    );

    if (cacheError) {
      console.error('Cache lookup error:', cacheError);
      throw new Error('Failed to check cached frost dates');
    }

    // If we got data from cache, return it
    if (cached) {
      return {
        lastSpring: cached.last_spring,
        firstFall: cached.first_fall
      };
    }

    // Get location from zip code
    const { lat, lon } = await getGeoLocation(zipCode);

    // Get nearest weather station
    const stationId = await getNearestStation(lat, lon);

    // Get spring and fall frost probabilities
    const [springProbs, fallProbs] = await Promise.all([
      getFrostProbabilities(stationId, 1), // Spring
      getFrostProbabilities(stationId, 2)  // Fall
    ]);

    // Format the dates properly
    const lastSpring = formatFrostDate(springProbs.prob_50);
    const firstFall = formatFrostDate(fallProbs.prob_50);

    // Cache the results
    try {
      await retryOperation(() =>
        supabase
          .from('frost_dates_cache')
          .insert({
            zip_code: zipCode,
            last_spring: lastSpring,
            first_fall: firstFall,
            created_at: new Date().toISOString()
          })
      );
    } catch (cacheError) {
      // Log but don't fail if caching fails
      console.warn('Failed to cache frost dates:', cacheError);
    }

    return { lastSpring, firstFall };
  } catch (error) {
    // Only log real errors, not expected "no data" responses
    if (!(error as any)?.body?.includes('PGRST116')) {
      console.error('Error fetching frost dates:', error);
    }
    throw error; // Re-throw to handle in the component
  }
}